import { useState } from 'react';
import { Heart, ShoppingBag, Eye, Bell } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LazyImage from '@/components/LazyImage';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';
import { GlobalProductCardControls } from '@/components/GlobalControls';
import { STOCK_INDICATORS } from '@/constants/stock-indicators';
// Stock notification form disabled - use external notifications instead

interface UnifiedProductCardProps {
  // Core product data
  id: string;
  title: string;
  description?: string;
  image: string;
  imageAlt: string;
  price: string;
  compareAtPrice?: string;
  
  // Product URL
  productUrl: string;
  
  // Variants (optional - for Shopify products)
  variants?: Array<{
    id: string;
    title: string;
    price: string;
    compareAtPrice?: string;
    image?: string;
    quantityAvailable?: number;
  }>;
  
  // Display options
  size?: 'large' | 'medium' | 'small';
  columnCount?: 2 | 3 | 4;
  viewSize?: 'small' | 'medium' | 'large';
  showPurchaseControls?: boolean;
  
  // Actions
  onAddToCart: (variantId?: string) => void;
  onToggleFavorite: () => void;
  onQuickView?: () => void;
  onImageLoad?: () => void;
  
  // State
  isFavorite: boolean;
  isInCart?: boolean;
  availableForSale?: boolean;
  
  // Badges
  badges?: Array<{
    label: string;
    variant: 'destructive' | 'secondary' | 'outline';
    className?: string;
  }>;
  
  // Tags
  tags?: string[];
}

export const UnifiedProductCard = ({
  id,
  title,
  description,
  image,
  imageAlt,
  price,
  compareAtPrice,
  productUrl,
  variants = [],
  size = 'large',
  columnCount,
  viewSize = 'large',
  showPurchaseControls = false,
  onAddToCart,
  onToggleFavorite,
  onQuickView,
  onImageLoad,
  isFavorite,
  isInCart = false,
  availableForSale = true,
  badges = [],
  tags = []
}: UnifiedProductCardProps) => {
  const [selectedVariantId, setSelectedVariantId] = useState(variants[0]?.id || '');
  const selectedVariant = variants.find(v => v.id === selectedVariantId) || variants[0];
  
  const isSmall = size === 'small';
  const isMedium = size === 'medium';
  const isLarge = size === 'large';
  // Get aspect ratio based on size
  const getAspectClass = () => {
    if (isLarge) return 'aspect-[4/5]';    // Large: Portrait format
    if (isMedium) return 'aspect-[5/4]';   // Medium: Slightly wider square (unchanged)
    return 'aspect-square';               // Small: Small squares
  };
  
  const hasVariants = variants.length > 1;
  const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.replace(/[$,]/g, '')) > parseFloat(price.replace(/[$,]/g, ''));
  
  // CRITICAL FIX: Trust product-level availableForSale flag from Shopify
  // Initial product queries only fetch 5-20 variants, but products may have 30+ variants
  // Calculating stock from incomplete variant data gives false "out of stock" results
  // Shopify's product-level flag is accurate - respect it
  const isOutOfStock = !availableForSale;
  
  // Low stock detection still useful when we have variant data
  const allVariantQuantities = variants.map(v => v.quantityAvailable || 0);
  const hasAnyStock = allVariantQuantities.some(qty => qty > 0);
  const hasAnyZeroStock = allVariantQuantities.some(qty => qty === 0);
  const isLowStock = availableForSale && hasAnyZeroStock && hasAnyStock; // Any variant at 0 but others have stock
  
  // Selected variant stock for display purposes
  const selectedVariantStock = selectedVariant?.quantityAvailable || 0;
  
  // Use variant data if available, otherwise use product data
  const currentPrice = selectedVariant?.price || price;
  const currentComparePrice = selectedVariant?.compareAtPrice || compareAtPrice;
  
  // OFFICIAL IMAGE SYNC: Always use the provided image prop first (maintains existing behavior)
  // Only use variant image selection for products with multiple images and variants
  const currentImage = image || selectedVariant?.image;
  
  // Unified discount calculation - handle currency formatting
  const getDiscountPercentage = () => {
    if (!currentComparePrice || !hasDiscount) return 0;
    const cleanCurrentPrice = parseFloat(currentPrice.replace(/[$,]/g, ''));
    const cleanComparePrice = parseFloat(currentComparePrice.replace(/[$,]/g, ''));
    if (cleanComparePrice > cleanCurrentPrice) {
      return Math.round(((cleanComparePrice - cleanCurrentPrice) / cleanComparePrice) * 100);
    }
    return 0;
  };
  
  const discountPercentage = getDiscountPercentage();

  const handleVariantChange = (variantId: string) => {
    setSelectedVariantId(variantId);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToCart(selectedVariantId);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite();
  };

  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🔍 UnifiedProductCard: QuickView clicked for product:', title);
    console.log('🔍 UnifiedProductCard: onQuickView function exists:', !!onQuickView);
    if (onQuickView) {
      console.log('🔍 UnifiedProductCard: Calling onQuickView...');
      onQuickView();
    } else {
      console.log('🔍 UnifiedProductCard: No onQuickView function provided!');
    }
  };

  // Calculate proper alignment for 2-column layout only
  const getTwoColumnAlignment = () => {
    if (isSmall && columnCount === 2) {
      // Left column (odd): fit to image width, right column (even): left-aligned with maintained spacing
      return {
        containerClass: '[&:nth-child(odd)]:w-full [&:nth-child(even)]:w-full',
        imageAlignment: '[&:nth-child(odd)]:justify-self-stretch [&:nth-child(even)]:justify-self-start',
        spacing: 'gap-2.5' // Consistent spacing between images
      };
    }
    return { containerClass: '', imageAlignment: '', spacing: '' };
  };

  const alignmentConfig = getTwoColumnAlignment();
  
  return (
    <Card className={`group overflow-hidden bg-card/80 backdrop-blur-sm border-border/20 shadow-lg hover:shadow-xl hover:shadow-primary/10 transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 ${
      isSmall && columnCount === 2 
        ? `w-full ${alignmentConfig.containerClass}` 
        : isSmall && columnCount >= 3
        ? 'w-full' // Remove max-w-xs constraint for 3+ columns
        : isSmall ? 'max-w-xs' : ''
    } ${isOutOfStock ? '!opacity-75 !saturate-50' : ''}`}>
      {/* Image Section */}
      <div className="relative overflow-hidden" onClick={(e) => console.log('🔍 Image container clicked:', title, e.target)}>
        {/* Subtle glass overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-popover/5 via-transparent to-background/5 z-10 pointer-events-none" aria-hidden="true" />
        <Link to={productUrl} onClick={(e) => {
          console.log('🔍 Product image link clicked:', title);
          sessionStorage.setItem('isBackNavigation', 'true');
        }}>
          <div className={`${getAspectClass()} bg-gradient-to-br from-muted/30 to-muted/10 border border-border/10 overflow-hidden ${
            isSmall && columnCount === 2 ? alignmentConfig.imageAlignment : ''
          }`}>
            <LazyImage
              src={currentImage}
              alt={imageAlt}
              width={isSmall ? 300 : 600}
              height={isSmall ? 375 : 600}
              sizes={isSmall ? "(max-width: 400px) 100vw, 300px" : "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"}
              className={`w-full h-full object-cover transition-opacity duration-300 hover:opacity-90 ${isOutOfStock ? '!grayscale-[25%] !opacity-90' : ''}`}
              priority={false}
              onLoad={onImageLoad}
            />
          </div>
        </Link>

        {/* Badges */}
        <div className={`absolute ${isSmall ? 'top-1 left-1' : 'top-3 left-3'} flex flex-col gap-1`}>
           {isOutOfStock && (
             <Badge variant="destructive" className={`${isSmall ? 'body-xs px-1' : 'body-xs'} weight-medium`}>
               Out of Stock
             </Badge>
           )}
           {!isOutOfStock && isLowStock && (
             <Badge variant="outline" className={`${isSmall ? 'body-xs px-1' : 'body-xs'} weight-medium bg-muted text-muted-foreground border-border`}>
               Low Stock
             </Badge>
           )}
           {/* Sale Badge - Enhanced visibility */}
           {hasDiscount && discountPercentage > 0 && (
             <Badge className={`${isSmall ? 'body-xs px-1' : 'body-xs'} weight-medium bg-accent/90 dark:bg-accent/70 text-accent-foreground border-transparent shadow-glow [&]:animate-none`}>
               {discountPercentage}% OFF
             </Badge>
           )}
           {badges.map((badge, index) => (
             <Badge key={index} variant={badge.variant} className={`${isSmall ? 'body-xs px-1' : 'body-xs'} weight-medium ${badge.className || ''}`}>
               {badge.label}
             </Badge>
           ))}
        </div>

        {/* Global Product Card Controls */}
        <GlobalProductCardControls
          productId={id}
          isFavorite={isFavorite}
          onToggleFavorite={handleFavoriteClick}
          onQuickView={handleQuickView}
          className={`${isSmall ? 'top-2 right-2' : 'top-3 right-3'}`}
        />
      </div>

      {/* REFINED TAB SECTION - Premium glassmorphism bottom */}
      <div className="bg-gradient-to-r from-card/90 to-card/70 backdrop-blur-md border-t border-border/20 relative overflow-hidden">
        {/* Subtle inner glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 opacity-60" aria-hidden="true" />
        <div className={`relative z-10 ${isSmall ? 'p-3 space-y-1' : 'p-4 space-y-3'}`}>
          <Link to={productUrl} onClick={() => sessionStorage.setItem('isBackNavigation', 'true')}>
            <h3 className="text-sm font-normal leading-normal text-foreground hover:text-primary-color transition-all duration-300 hover:scale-[1.01]">
              {title} <span className={`text-sm font-normal ${hasDiscount ? 'text-accent-color' : 'text-primary-color'}`}>${parseFloat(currentPrice.replace(/[$,]/g, '')).toFixed(2)}</span>
              {hasDiscount && currentComparePrice && (
                <span className="text-sm text-muted-color line-through ml-2">
                  ${parseFloat(currentComparePrice.replace(/[$,]/g, '')).toFixed(2)}
                </span>
              )}
            </h3>
          </Link>

          {/* Discount info moved below title/price line */}
          {/* Removed - discount already shown in badge */}


          {/* Purchase Controls - Only show when enabled */}
          {showPurchaseControls && (
            isOutOfStock ? (
               /* Stock notification disabled - show disabled button */
               <Button
                 disabled
                 variant="outline"
                 className="w-full border-primary/30 text-muted-foreground"
                 size={isSmall ? "sm" : "default"}
               >
                 <Bell className={`${isSmall ? 'w-3 h-3' : 'w-4 h-4'} mr-2`} />
                 Out of Stock
               </Button>
            ) : hasVariants ? (
              <div className="flex items-center gap-2">
                <Select value={selectedVariantId} onValueChange={handleVariantChange}>
                  <SelectTrigger className="bg-background border-border flex-1 min-w-0">
                    <SelectValue placeholder="Select variant" />
                  </SelectTrigger>
                  <SelectContent className="z-50 bg-popover border border-border shadow-lg">
                    {variants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.id}>
                        <div className="flex justify-between items-center w-full">
                          <span>{variant.title}</span>
                          <span className="ml-2 font-medium">{variant.price}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                 <Button
                   onClick={handleAddToCart}
                   className="btn-glass whitespace-nowrap"
                   size={isSmall ? "sm" : "default"}
                 >
                   <ShoppingBag className={`${isSmall ? 'w-3 h-3' : 'w-4 h-4'} mr-2`} />
                   Add
                 </Button>
              </div>
            ) : (
               <Button
                 onClick={handleAddToCart}
                 className="w-full btn-glass"
                 size={isSmall ? "sm" : "default"}
               >
                 <ShoppingBag className={`${isSmall ? 'w-3 h-3' : 'w-4 h-4'} mr-2`} />
                 {isInCart ? 'In Cart' : 'Add to Cart'}
               </Button>
            )
          )}

          {/* Stock notification modal disabled */}
        </div>
      </div>
    </Card>
  );
};